import { useState } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { apiFetch } from '@/lib/api';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';

/** v1.0 (2026-08-06): v0.1由来の audit_log permission (company_adminロールに割当済み) に対応する
 *  画面が無かったギャップを埋める新規ページ。GET /platform/audit-logs (PlatformAdminPage.tsx、
 *  cross_tenant_admin専用) とは別物で、こちらはテナント内の通常CRUD操作の監査ログを表示する。 */

type AuditLogEntry = {
  id: string;
  action_type: string;
  target_table: string | null;
  target_id: string | null;
  user_id: string | null;
  user_display_name: string | null;
  actor_employee_id: string | null;
  actor_employee_name: string | null;
  actor_external_person_id: string | null;
  actor_external_person_name: string | null;
  actor_user_type: string | null;
  before_value: Record<string, unknown> | null;
  after_value: Record<string, unknown> | null;
  ip_address: string | null;
  created_at: string;
};

// 操作者表示の優先順位: 社員 > 外部人材 > ユーザーアカウント (docs/design/v10-tenant-audit-log.md §2)
function actorLabel(log: AuditLogEntry): string {
  if (log.actor_employee_name) return log.actor_employee_name;
  if (log.actor_external_person_name) return log.actor_external_person_name;
  return log.user_display_name ?? log.user_id ?? '—';
}

const AUDIT_LOG_LIMIT = 50;

// action_type はルート実装ごとの自由記述 (create/update/delete が大半、一部 import/rollback/renew 等の
// 固有動詞もある) のため、feature別の網羅的な辞書は作らない (§8-55型の更新漏れリスク回避)。
// 汎用的なCRUD動詞のみ日本語化し、それ以外はraw値をそのまま表示する。
const ACTION_TYPE_LABEL: Record<string, string> = {
  create: '作成',
  update: '更新',
  delete: '削除',
};

function AuditLogDetail({ log }: { log: AuditLogEntry }) {
  if (!log.before_value && !log.after_value) return <span>—</span>;
  const summary = JSON.stringify({ before: log.before_value, after: log.after_value });
  return (
    <span className="font-mono text-xs" title={summary}>
      {log.before_value && log.after_value ? '変更あり' : log.after_value ? '新規登録' : '削除内容あり'}
    </span>
  );
}

export function AuditLogsPage() {
  const [targetTable, setTargetTable] = useState('');
  const [actionType, setActionType] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  const { data, isLoading, isError, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteQuery<
    { logs: AuditLogEntry[]; has_more: boolean }
  >({
    queryKey: ['audit-logs', targetTable, actionType, from, to],
    queryFn: ({ pageParam }) => {
      const params = new URLSearchParams();
      if (targetTable) params.set('target_table', targetTable);
      if (actionType) params.set('action_type', actionType);
      if (from) params.set('from', from);
      if (to) params.set('to', to);
      params.set('limit', String(AUDIT_LOG_LIMIT));
      params.set('offset', String(pageParam));
      return apiFetch(`/audit-logs?${params.toString()}`);
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => (lastPage.has_more ? allPages.length * AUDIT_LOG_LIMIT : undefined),
  });

  const logs = data?.pages.flatMap((p) => p.logs) ?? [];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-h1">監査ログ</h1>
        <p className="text-sm text-text-sub mt-1">
          マスタ編集・データ取込等の操作履歴を確認できます
        </p>
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <div>
          <label htmlFor="audit-log-action-type" className="block text-xs text-text-sub mb-1">操作種別</label>
          <Select value={actionType || 'all'} onValueChange={(v) => setActionType(v === 'all' ? '' : v)}>
            <SelectTrigger id="audit-log-action-type" className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">すべて</SelectItem>
              <SelectItem value="create">作成</SelectItem>
              <SelectItem value="update">更新</SelectItem>
              <SelectItem value="delete">削除</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <label htmlFor="audit-log-target-table" className="block text-xs text-text-sub mb-1">対象テーブル</label>
          <input
            id="audit-log-target-table"
            type="text"
            value={targetTable}
            onChange={(e) => setTargetTable(e.target.value)}
            placeholder="例: menu_master"
            className="min-h-[44px] px-3 py-1.5 border border-surface-border rounded text-sm bg-surface-card w-44"
          />
        </div>
        <div>
          <label htmlFor="audit-log-from" className="block text-xs text-text-sub mb-1">期間 (開始)</label>
          <input
            id="audit-log-from"
            type="date"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            className="min-h-[44px] px-3 py-1.5 border border-surface-border rounded text-sm bg-surface-card"
          />
        </div>
        <div>
          <label htmlFor="audit-log-to" className="block text-xs text-text-sub mb-1">期間 (終了)</label>
          <input
            id="audit-log-to"
            type="date"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            className="min-h-[44px] px-3 py-1.5 border border-surface-border rounded text-sm bg-surface-card"
          />
        </div>
      </div>

      <div className="border border-surface-line rounded-lg bg-surface-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[720px] text-sm">
            <thead className="text-text-sub bg-surface-sunken/50 text-left">
              <tr>
                <th scope="col" className="relative px-3 py-2 w-40">発生日時</th>
                <th scope="col" className="relative px-3 py-2">操作</th>
                <th scope="col" className="relative px-3 py-2">対象</th>
                <th scope="col" className="relative px-3 py-2">操作者</th>
                <th scope="col" className="relative px-3 py-2">詳細</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr><td colSpan={5} className="px-3 py-6 text-center text-text-sub">読み込み中…</td></tr>
              )}
              {!isLoading && isError && (
                <tr><td colSpan={5} className="px-3 py-6 text-center text-alert-text">読み込みに失敗しました。時間をおいて再度お試しください</td></tr>
              )}
              {!isLoading && !isError && logs.length === 0 && (
                <tr><td colSpan={5} className="px-3 py-6 text-center text-text-sub">該当する監査ログがありません</td></tr>
              )}
              {logs.map((log) => (
                <tr key={log.id} className="border-t border-surface-line align-top">
                  <td className="px-3 py-2 text-text-sub whitespace-nowrap">
                    {new Date(log.created_at).toLocaleString('ja-JP')}
                  </td>
                  <td className="px-3 py-2">{ACTION_TYPE_LABEL[log.action_type] ?? log.action_type}</td>
                  <td className="px-3 py-2 text-text-sub">
                    {log.target_table ?? '—'}
                    {log.target_id && <span className="text-text-muted"> ({log.target_id})</span>}
                  </td>
                  <td className="px-3 py-2 text-text-sub">{actorLabel(log)}</td>
                  <td className="px-3 py-2 text-text-sub"><AuditLogDetail log={log} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {hasNextPage && (
        <div className="flex justify-center">
          <button
            type="button"
            onClick={() => fetchNextPage()}
            disabled={isFetchingNextPage}
            className="min-h-[44px] px-4 py-2 rounded border border-surface-border text-sm hover:bg-surface-sunken disabled:opacity-50"
          >
            {isFetchingNextPage ? '読み込み中…' : 'もっと見る'}
          </button>
        </div>
      )}
    </div>
  );
}
